import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, merge } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';
import { saveAs } from 'file-saver';

import * as XLSX from 'xlsx';

import { CustomerService } from '../../services/customer.service';
import { OrdersService } from '../../services/orders.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-batch-invoice-print',
	templateUrl: './batch-invoice-print.component.html',
	styleUrls: ['./batch-invoice-print.component.scss'],
})
export class BatchInvoicePrintComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	private batchCancel$ = new Subject<void>();

	/** Theme token for `card-header-{{headerColor}}-6` (matches customer view / cdet). */
	headerColor = 'blue';

	customer: { debtorno: string; branchcode: string; name?: string } | null = null;
	fromdateCtrl = new UntypedFormControl('');
	todateCtrl = new UntypedFormControl('');
	opentrans = new UntypedFormControl(false);
	translimit = new UntypedFormControl('500');
	tableFilter = new UntypedFormControl('');
	typeScope: 'both' | 'invoice' | 'credit' = 'both';

	loading = false;
	printing = false;
	downloadProgress = 0;
	batchDocProgress = { current: 0, total: 0, label: '' };
	batchCancelled = false;

	displayedColumns: string[] = ['select', 'trandate', 'typename', 'transno', 'order_', 'totalamount', 'balance'];
	dataSource = new MatTableDataSource<any>([]);
	selection = new SelectionModel<any>(true, []);

	// XLSX upload mode
	mode: 'customer' | 'upload' = 'customer';
	uploadFiles: File[] = [];
	uploadColumns: string[] = [];
	uploadRows: Record<string, any>[] = [];
	uploadOrderColumn = '';
	uploadOrders: string[] = [];
	uploadParsing = false;

	constructor(
		private customerService: CustomerService,
		private ordersService: OrdersService,
		private globalSearchService: GlobalSearchService,
		private route: ActivatedRoute,
		private router: Router,
		private location: Location,
		private cdr: ChangeDetectorRef,
	) {
		this.dataSource.filterPredicate = (row: any, filter: string) => {
			const q = filter.trim().toLowerCase();
			const typeOk = this.rowMatchesTypeScope(row);
			if (!typeOk) {
				return false;
			}
			if (!q) {
				return true;
			}
			const hay = [
				row.transno,
				row.order_,
				row.typename,
				row.invtext,
				row.trandate,
			]
				.map((v) => (v != null ? String(v).toLowerCase() : ''))
				.join(' ');
			return hay.includes(q);
		};
	}

	ngOnInit(): void {
		this.headerColor = this.globalSearchService.getColor();

		const today = new Date();
		const yearAgo = new Date();
		yearAgo.setFullYear(today.getFullYear() - 1);
		this.todateCtrl.setValue(this.toYmd(today));
		this.fromdateCtrl.setValue(this.toYmd(yearAgo));

		this.tableFilter.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.applyTableFilter();
		});

		const nav = this.router.getCurrentNavigation();
		const st = nav?.extras?.state as { customer?: any } | undefined;
		if (st?.customer?.debtorno) {
			this.applyCustomerRow(st.customer);
		} else {
			const d = this.route.snapshot.queryParamMap.get('debtorno');
			const b = this.route.snapshot.queryParamMap.get('branchcode');
			if (d) {
				this.prefillFromDebtorno(d, b || d);
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private toYmd(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private applyCustomerRow(row: any): void {
		if (!row?.debtorno) {
			return;
		}
		this.customer = {
			debtorno: String(row.debtorno),
			branchcode: String(row.branchcode || row.debtorno),
			name: row.name,
		};
	}

	onCustomerSelected(row: any): void {
		this.applyCustomerRow(row);
		this.selection.clear();
		this.dataSource.data = [];
	}

	clearCustomer(): void {
		this.customer = null;
		this.selection.clear();
		this.dataSource.data = [];
	}

	back(): void {
		this.location.back();
	}

	private prefillFromDebtorno(debtorno: string, branchcode: string): void {
		this.customerService.getCustomer(debtorno, branchcode).pipe(takeUntil(this.destroy$)).subscribe({
			next: (c: any) => {
				const row = c?.customer || c;
				if (row?.debtorno) {
					this.customer = {
						debtorno: String(row.debtorno),
						branchcode: String(row.branchcode || branchcode),
						name: row.name,
					};
				}
			},
			error: () => {
				this.globalSearchService.showNotification('Could not load customer from link.', 'danger', 'bottom', 'center');
			},
		});
	}

	private rowMatchesTypeScope(row: any): boolean {
		const t = String(row.type);
		if (this.typeScope === 'invoice') {
			return t === '10';
		}
		if (this.typeScope === 'credit') {
			return t === '11';
		}
		return t === '10' || t === '11';
	}

	isPrintableRow(row: any): boolean {
		return this.rowMatchesTypeScope(row);
	}

	selectableRows(): any[] {
		return this.dataSource.filteredData.filter((r) => this.isPrintableRow(r));
	}

	isAllSelected(): boolean {
		const rows = this.selectableRows();
		return rows.length > 0 && rows.every((r) => this.selection.isSelected(r));
	}

	isSomeSelected(): boolean {
		const rows = this.selectableRows();
		const n = rows.filter((r) => this.selection.isSelected(r)).length;
		return n > 0 && n < rows.length;
	}

	masterToggle(checked: boolean): void {
		if (checked) {
			this.selectableRows().forEach((r) => this.selection.select(r));
		} else {
			this.selection.clear();
		}
	}

	onRowCheckboxChange(checked: boolean, row: any): void {
		if (checked) {
			this.selection.select(row);
		} else {
			this.selection.deselect(row);
		}
	}

	onTypeScopeChange(): void {
		this.selection.clear();
		this.dataSource.data = [...(this.dataSource.data || [])];
		this.applyTableFilter();
	}

	applyTableFilter(): void {
		const v = (this.tableFilter.value || '').toString().toLowerCase();
		this.dataSource.filter = v;
	}

	/** Trim the rows the server returned to the From/To range the user actually picked — the AR transactions endpoint occasionally ignores the date filter (open-only/aging branches), so we enforce it client-side. */
	private applyDateRangeFilter(rows: any[]): any[] {
		const from = (this.fromdateCtrl.value || '').toString();
		const to = (this.todateCtrl.value || '').toString();
		if (!from && !to) {
			return rows;
		}
		const parse = (s: any): string => {
			if (!s) return '';
			const str = String(s);
			// trandate_raw is "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS"
			const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
			// formatted "MM/DD/YYYY" or "M/D/YYYY"
			const us = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
			if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
			// fallback: let Date parse it
			const d = new Date(str);
			if (!isNaN(d.getTime())) {
				const y = d.getFullYear();
				const m = String(d.getMonth() + 1).padStart(2, '0');
				const day = String(d.getDate()).padStart(2, '0');
				return `${y}-${m}-${day}`;
			}
			return '';
		};
		return rows.filter((r) => {
			const d = parse(r.trandate_raw || r.trandate);
			if (!d) return true; // don't drop unparseable
			if (from && d < from) return false;
			if (to && d > to) return false;
			return true;
		});
	}

	loadTransactions(): void {
		if (!this.customer) {
			return;
		}
		this.loading = true;
		this.selection.clear();

		let types: number[];
		if (this.typeScope === 'invoice') {
			types = [10];
		} else if (this.typeScope === 'credit') {
			types = [11];
		} else {
			types = [10, 11];
		}

		const payload: any = {
			fromdate: this.fromdateCtrl.value,
			todate: this.todateCtrl.value,
			debtorno: this.customer.debtorno,
			opentrans: this.opentrans.value ? true : false,
			types,
			transno: '',
			keywords: '',
			age: '',
			limit: this.translimit.value || '500',
		};

		this.customerService
			.getDebtorTransactionsRange(payload)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.loading = false;
				}),
			)
			.subscribe({
				next: (res: any) => {
					const rows = Array.isArray(res?.transactions) ? res.transactions : [];
					this.dataSource.data = this.applyDateRangeFilter(rows);
					this.applyTableFilter();
				},
				error: () => {
					this.globalSearchService.showNotification('Failed to load transactions.', 'danger', 'bottom', 'center');
				},
			});
	}

	get batchDocProgressPercent(): number {
		if (!this.batchDocProgress.total) {
			return 0;
		}
		return Math.round((100 * this.batchDocProgress.current) / this.batchDocProgress.total);
	}

	cancelBatchPrint(): void {
		if (!this.printing) {
			return;
		}
		this.batchCancelled = true;
		this.batchCancel$.next();
		this.touchProgress();
	}

	private touchProgress(): void {
		this.cdr.detectChanges();
	}

	private docLabel(row: { typename?: string; transno?: string; order_?: string }): string {
		const type = row.typename || 'Document';
		const parts = [type];
		if (row.transno != null && row.transno !== '') {
			parts.push('#' + row.transno);
		}
		if (row.order_ != null && row.order_ !== '') {
			parts.push('order ' + row.order_);
		}
		return parts.join(' ');
	}

	/** Existing batch endpoint; pass one item to drive per-document progress without new server APIs. */
	private batchPostToBlob(debtorno: string, items: { id: string }[]): Promise<Blob | null> {
		return new Promise((resolve) => {
			this.ordersService
				.batchCustomerInvoiceCreditPdf(debtorno, items)
				.pipe(takeUntil(merge(this.destroy$, this.batchCancel$)))
				.subscribe({
					next: async (ev) => {
						if (ev.type === HttpEventType.DownloadProgress && ev.total) {
							this.downloadProgress = Math.round((100 * ev.loaded) / ev.total);
							this.touchProgress();
						}
						if (ev.type === HttpEventType.Response && ev.body instanceof Blob) {
							resolve(await this.blobOrNull(ev.body, ev.headers?.get('content-type') || ''));
						}
					},
					error: () => resolve(null),
				});
		});
	}

	private async generateBatchDocuments(
		debtorno: string,
		rows: Array<{ id: string; type: string | number; typename?: string; transno?: string; order_?: string }>,
	): Promise<boolean> {
		this.batchDocProgress = { current: 0, total: rows.length, label: 'Starting…' };
		this.touchProgress();

		for (const row of rows) {
			if (this.batchCancelled) {
				return false;
			}
			this.batchDocProgress.label = 'Creating ' + this.docLabel(row);
			this.touchProgress();
			const blob = await this.batchPostToBlob(debtorno, [{ id: String(row.id) }]);
			if (!blob) {
				if (this.batchCancelled) {
					return false;
				}
				this.globalSearchService.showNotification(
					'Failed generating ' + this.docLabel(row) + '.',
					'danger',
					'bottom',
					'center',
				);
				return false;
			}
			this.batchDocProgress.current += 1;
			this.touchProgress();
		}
		return !this.batchCancelled;
	}

	private async blobOrNull(blob: Blob, contentType: string): Promise<Blob | null> {
		const ct = (contentType || '').toLowerCase();
		if (ct.includes('application/json') || ct.includes('text/json') || blob.size < 100 || ct.includes('text/html')) {
			const text = await blob.text();
			let msg = 'Batch print failed.';
			try {
				const j = JSON.parse(text);
				msg = j.error || j.message || msg;
			} catch {
				if (text) {
					msg = text;
				}
			}
			this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'center');
			return null;
		}
		return blob;
	}

	async batchPrint(): Promise<void> {
		if (!this.customer || this.selection.selected.length === 0 || this.printing) {
			return;
		}
		const rows = this.selection.selected.filter((r) => this.isPrintableRow(r));
		if (rows.length === 0) {
			this.globalSearchService.showNotification('Select at least one invoice or credit.', 'warning', 'bottom', 'center');
			return;
		}

		this.printing = true;
		this.batchCancelled = false;
		this.downloadProgress = 0;
		this.batchDocProgress = { current: 0, total: rows.length, label: 'Starting…' };
		this.touchProgress();
		const items = rows.map((r) => ({ id: String(r.id) }));

		try {
			if (rows.length === 1) {
				this.batchDocProgress.label = 'Creating ' + this.docLabel(rows[0]);
				this.touchProgress();
				const blob = await this.batchPostToBlob(this.customer.debtorno, items);
				if (!blob || this.batchCancelled) {
					if (this.batchCancelled) {
						this.globalSearchService.showNotification('Batch print cancelled.', 'warning', 'bottom', 'center');
					}
					return;
				}
				const name = `Invoices-${this.customer.debtorno}-${this.toYmd(new Date())}.pdf`;
				saveAs(blob, name);
				this.globalSearchService.showNotification('PDF downloaded.', 'success', 'bottom', 'center');
				return;
			}

			const ok = await this.generateBatchDocuments(this.customer.debtorno, rows);
			if (!ok) {
				if (this.batchCancelled) {
					this.globalSearchService.showNotification('Batch print cancelled.', 'warning', 'bottom', 'center');
				}
				return;
			}
			this.batchDocProgress.label = 'Merging and downloading…';
			this.batchDocProgress.current = rows.length;
			this.touchProgress();
			const blob = await this.batchPostToBlob(this.customer.debtorno, items);
			if (!blob || this.batchCancelled) {
				if (this.batchCancelled) {
					this.globalSearchService.showNotification('Batch print cancelled.', 'warning', 'bottom', 'center');
				}
				return;
			}
			const name = `Invoices-${this.customer.debtorno}-${this.toYmd(new Date())}.pdf`;
			saveAs(blob, name);
			this.globalSearchService.showNotification('PDF downloaded.', 'success', 'bottom', 'center');
		} finally {
			this.printing = false;
			this.downloadProgress = 0;
			this.batchDocProgress = { current: 0, total: 0, label: '' };
			this.touchProgress();
		}
	}

	onModeChange(mode: 'customer' | 'upload'): void {
		this.mode = mode;
		if (mode === 'upload') {
			this.clearCustomer();
		} else {
			this.clearUpload();
		}
	}

	clearUpload(): void {
		this.uploadFiles = [];
		this.uploadColumns = [];
		this.uploadRows = [];
		this.uploadOrderColumn = '';
		this.uploadOrders = [];
		this.uploadParsing = false;
	}

	onUploadDropzoneSelect(event: { addedFiles: File[]; rejectedFiles?: File[] }): void {
		const file = event?.addedFiles?.[0];
		if (!file) {
			return;
		}
		this.uploadFiles = [file];
		this.parseUploadFile(file);
	}

	onUploadDropzoneRemove(_file: File): void {
		this.clearUpload();
	}

	formatFileSizeKb(bytes: number): string {
		return ((bytes || 0) / 1024).toFixed(1);
	}

	private parseUploadFile(file: File): void {
		this.uploadParsing = true;
		this.uploadColumns = [];
		this.uploadRows = [];
		this.uploadOrderColumn = '';
		this.uploadOrders = [];
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target!.result as ArrayBuffer);
				const wb = XLSX.read(data, { type: 'array' });
				const ws = wb.Sheets[wb.SheetNames[0]];
				const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
				if (!rows.length) {
					this.globalSearchService.showNotification('File is empty.', 'warning', 'bottom', 'center');
					this.clearUpload();
					return;
				}
				this.uploadRows = rows;
				this.uploadColumns = Object.keys(rows[0]);
				const guess = this.uploadColumns.find((c) => /order|orderno|order_?#|order\s*number/i.test(c));
				this.uploadOrderColumn = guess || this.uploadColumns[0];
				this.recomputeUploadOrders();
			} catch {
				this.globalSearchService.showNotification('Could not parse file.', 'danger', 'bottom', 'center');
				this.clearUpload();
			} finally {
				this.uploadParsing = false;
			}
		};
		reader.readAsArrayBuffer(file);
	}

	onUploadColumnChange(): void {
		this.recomputeUploadOrders();
	}

	private recomputeUploadOrders(): void {
		if (!this.uploadOrderColumn) {
			this.uploadOrders = [];
			return;
		}
		const seen = new Set<string>();
		const out: string[] = [];
		for (const row of this.uploadRows) {
			const v = row[this.uploadOrderColumn];
			if (v == null) {
				continue;
			}
			const s = String(v).trim();
			if (!s || seen.has(s)) {
				continue;
			}
			seen.add(s);
			out.push(s);
		}
		this.uploadOrders = out;
	}

	async bulkPrintFromUpload(): Promise<void> {
		if (this.printing || this.uploadOrders.length === 0) {
			return;
		}
		this.printing = true;
		this.batchCancelled = false;
		this.downloadProgress = 0;

		try {
			// 1. order# -> debtorno via orders/search
			const orderToDebtor: Record<string, string> = {};
			const notFound: string[] = [];
			for (const o of this.uploadOrders) {
				try {
					const res: any = await this.ordersService.getOrderSearch(o).toPromise();
					const list = Array.isArray(res) ? res : [];
					const hit = list.find((r: any) => String(r.orderno) === String(o)) || list[0];
					if (hit?.debtorno) {
						orderToDebtor[o] = String(hit.debtorno);
					} else {
						notFound.push(o);
					}
				} catch {
					notFound.push(o);
				}
			}

			const debtorList = Array.from(new Set(Object.values(orderToDebtor)));
			if (debtorList.length === 0) {
				this.globalSearchService.showNotification('None of the order numbers matched a customer.', 'danger', 'bottom', 'center');
				return;
			}

			// 2. For each customer: pull invoices, match order#s, call existing batch endpoint, save PDF.
			let savedCount = 0;
			let printedOrders = 0;
			const stamp = this.toYmd(new Date());
			for (const debtorno of debtorList) {
				if (this.batchCancelled) {
					break;
				}
				const ordersForDebtor = Object.entries(orderToDebtor)
					.filter(([, d]) => d === debtorno)
					.map(([o]) => o);

				let trans: any;
				try {
					trans = await this.customerService
						.getDebtorTransactionsRange({
							fromdate: '',
							todate: '',
							debtorno,
							opentrans: true,
							types: [10],
							transno: '',
							keywords: '',
							age: '',
							limit: '5000',
						})
						.toPromise();
				} catch {
					continue;
				}

				const rows = (Array.isArray(trans?.transactions) ? trans.transactions : []).filter(
					(r: any) => String(r.type) === '10' && ordersForDebtor.includes(String(r.order_)),
				);
				if (!rows.length) {
					continue;
				}
				const blob = await this.batchForCustomerToBlob(debtorno, rows);
				if (!blob) {
					if (this.batchCancelled) {
						break;
					}
					continue;
				}
				const name = `Invoices-${debtorno}-${stamp}.pdf`;
				saveAs(blob, name);
				savedCount++;
				printedOrders += rows.length;
			}

			if (savedCount === 0) {
				this.globalSearchService.showNotification('No invoices could be generated for the supplied orders.', 'danger', 'bottom', 'center');
			} else {
				const missingNote = notFound.length ? ` ${notFound.length} order # not matched.` : '';
				this.globalSearchService.showNotification(
					`${savedCount} PDF(s) saved · ${printedOrders} invoice(s) across ${debtorList.length} customer(s).${missingNote}`,
					'success',
					'bottom',
					'center',
				);
			}
		} finally {
			this.printing = false;
			this.downloadProgress = 0;
			this.batchDocProgress = { current: 0, total: 0, label: '' };
		}
	}

	private async batchForCustomerToBlob(
		debtorno: string,
		rows: Array<{ id: string; type: string | number; typename?: string; transno?: string; order_?: string }>,
	): Promise<Blob | null> {
		const items = rows.map((r) => ({ id: String(r.id) }));
		if (rows.length === 1) {
			return this.batchPostToBlob(debtorno, items);
		}
		const ok = await this.generateBatchDocuments(debtorno, rows);
		if (!ok || this.batchCancelled) {
			return null;
		}
		this.batchDocProgress.label = 'Merging and downloading…';
		this.batchDocProgress.current = rows.length;
		this.touchProgress();
		return this.batchPostToBlob(debtorno, items);
	}
}
